// hooks/useSessionsModal.js
import { useState, useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  membershipPackagesAPI,
  sessionsAPI,
  studentSubscriptionsAPI,
  organizationsAPI,
  notificationsAPI,
  sessionTypesAPI,
  studentsAPI,
  trainersAPI,
} from "../services/api";
import { useAuth } from "../App";
import { usePermissions } from "../components/HasPermission";

export const useSessionsModal = (
  organizationId,
  students,
  trainers,
  loadSessions,
) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create"); // 'create', 'edit', 'view'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Organizations data (for when viewing 'all' organizations)
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  // Session Types data
  const [sessionTypes, setSessionTypes] = useState([]); // Kept for backward compatibility (merged)
  const [mainSessionTypes, setMainSessionTypes] = useState([]); // Main Categories (e.g. Reformer)
  const [subPackages, setSubPackages] = useState([]); // Sub Packages (e.g. Reformer 10lu)
  const [loadingSessionTypes, setLoadingSessionTypes] = useState(false);

  // Student Subscriptions (for filtering)
  const [studentSubscriptions, setStudentSubscriptions] = useState([]);
  const [loadingStudentSubscriptions, setLoadingStudentSubscriptions] =
    useState(false);

  // Local state for students and trainers (to handle organization switching in modal)
  const [modalStudents, setModalStudents] = useState(students || []);
  const [modalTrainers, setModalTrainers] = useState(trainers || []);
  const [loadingModalData, setLoadingModalData] = useState(false);

  // Check if we're in "all organizations" mode
  const isAllOrgsMode = organizationId === "all";



  // Form data
  const [formData, setFormData] = useState({
    selectedOrganizationId: "", // For when viewing 'all' organizations
    trainerId: "",
    students: [], // Array of student IDs
    sessionTypeId: "", // Specific Package or Session Type ID (The Sub Category)
    mainTypeId: "", // The Main Category ID (New)
    startTime: moment()
      .hours(9)
      .minutes(0)
      .seconds(0)
      .format("YYYY-MM-DDTHH:mm:ss"),
    endTime: moment()
      .hours(10)
      .minutes(0)
      .seconds(0)
      .format("YYYY-MM-DDTHH:mm:ss"),
    notes: "",
    status: "scheduled",
  });

  // Compute if currently selected mainType is a Demo
  const isDemoSession = useMemo(() => {
    const mainType = mainSessionTypes.find(t => t.id === formData.mainTypeId);
    if (!mainType) return false;
    // Prefer the explicit isDemo flag; fall back to the legacy name-substring match
    // only when the flag isn't set (older session types).
    return mainType.isDemo ?? mainType.name.toLowerCase().includes('demo');
  }, [formData.mainTypeId, mainSessionTypes]);

  // Multi-session creation
  const [sessionTimes, setSessionTimes] = useState([
    {
      id: 1,
      startTime: moment()
        .hours(9)
        .minutes(0)
        .seconds(0)
        .format("YYYY-MM-DDTHH:mm:ss"),
      endTime: moment()
        .hours(10)
        .minutes(0)
        .seconds(0)
        .format("YYYY-MM-DDTHH:mm:ss"),
    },
  ]);

  // Reset form data
  const resetFormData = useCallback(() => {
    const defaultTime = moment().hours(9).minutes(0).seconds(0);

    setFormData({
      selectedOrganizationId: "",
      trainerId: "",
      students: [],
      sessionTypeId: "",
      mainTypeId: "",
      startTime: defaultTime.format("YYYY-MM-DDTHH:mm:ss"),
      endTime: defaultTime.clone().add(55, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
      notes: "",
      status: "scheduled",
    });

    setSessionTimes([
      {
        id: 1,
        startTime: defaultTime.format("YYYY-MM-DDTHH:mm:ss"),
        endTime: defaultTime.clone().add(55, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
      },
    ]);

    setFormErrors({});
    setFormError(null);
    setSessionTypes([]);
    setMainSessionTypes([]);
    setSubPackages([]);
    setStudentSubscriptions([]);
    setOrganizations([]);
    setModalStudents(students || []);
    setModalTrainers(trainers || []);
  }, [trainers, students]);

  // Load organizations (for 'all' mode)
  const loadOrganizations = useCallback(async () => {
    if (!isAllOrgsMode) return;

    setLoadingOrganizations(true);
    try {
      const response = await organizationsAPI.getAll();
      const orgs = response.data.data || response.data || [];
      const realOrgs = orgs.filter(
        (org) =>
          org.id !== "all" &&
          org.name !== "All Organizations" &&
          org.name !== "Tüm Organizasyonlar",
      );
      setOrganizations(realOrgs);

      // Auto-select first real organization if none is selected
      if (realOrgs.length > 0 && !formData.selectedOrganizationId) {
        setFormData((prev) => ({
          ...prev,
          selectedOrganizationId: realOrgs[0].id,
        }));
        loadSessionTypes(realOrgs[0].id);
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
      setFormError(t("sessionsPage.messages.loadOrgsError"));
    } finally {
      setLoadingOrganizations(false);
    }
  }, [isAllOrgsMode, t]);

  // Load session types and packages
  const loadSessionTypes = useCallback(
    async (orgId) => {
      // Use provided orgId, or selectedOrganizationId from form, or the default organizationId
      const targetOrgId =
        orgId || formData.selectedOrganizationId || organizationId;
      if (!targetOrgId || targetOrgId === "all") {
        setSessionTypes([]);
        setMainSessionTypes([]);
        setSubPackages([]);
        return;
      }

      setLoadingSessionTypes(true);

      try {
        // Fetch Session Types, Membership Packages, Students, and Trainers in parallel
        const [
          typesResponse,
          pkgsResponse,
          studentsResponse,
          trainersResponse,
        ] = await Promise.all([
          sessionTypesAPI
            .getAllByOrg(targetOrgId)
            .catch(() => ({ data: { data: [] } })),
          membershipPackagesAPI
            .getAllByOrg(targetOrgId)
            .catch(() => ({ data: { data: [] } })),
          studentsAPI
            .getAll(targetOrgId, 2000)
            .catch(() => ({ data: { data: [] } })),
          trainersAPI
            .getAll(targetOrgId, 2000)
            .catch(() => ({ data: { data: [] } })),
        ]);

        const allTypes = typesResponse.data.data || typesResponse.data || [];
        const rawPkgs = pkgsResponse.data.data || pkgsResponse.data || [];

        // Update Students and Trainers
        const allFetchedStudents = studentsResponse.data.data || [];
        const allFetchedTrainers = trainersResponse.data.data || [];
        setModalStudents(allFetchedStudents.filter(s => s.studentStatus === 'active'));
        setModalTrainers(allFetchedTrainers.filter(t => t.status === 'active'));

        // Store Separately
        setMainSessionTypes(allTypes);

        const normalizedPkgs = rawPkgs.map((p) => {
          const name = p.name || p.packageName || p.title || "Unknown Package";
          
          let maxParticipants = null;

          // 1. ALWAYS Try to get parent session type first (authoritative source if user updated it in organization settings)
          const parentType = allTypes.find(t => t.id === p.sessionTypeId);
          if (parentType && parentType.maxParticipants) {
            maxParticipants = parentType.maxParticipants;
          }
          
          // 2. Try regex (explicit package name override, e.g., "2'li") if parent doesn't enforce one
          if (!maxParticipants) {
            const match = name.match(/(\d+).?l[üiuı]/i);
            if (match) {
              maxParticipants = parseInt(match[1], 10);
            }
          }
          
          // 3. Fallback to the saved package value if all else fails
          if (!maxParticipants) {
            maxParticipants = p.maxParticipants || 1;
          }
          
          return {
            ...p,
            name,
            id: p.id,
            isPackage: true,
            sessionTypeId: p.sessionTypeId,
            maxParticipants,
          };
        });
        setSubPackages(normalizedPkgs);

        // Normalize Types for merged list (legacy support)
        const normalizedTypes = allTypes.map((t) => ({
          ...t,
          isPackage: false,
        }));

        setSessionTypes([...normalizedTypes, ...normalizedPkgs]);
      } catch (error) {
        console.error("Error loading session types:", error);
        setFormError(t("sessionsPage.messages.loadPackagesError"));
        setSessionTypes([]);
        setMainSessionTypes([]);
        setSubPackages([]);
      } finally {
        setLoadingSessionTypes(false);
      }
    },
    [organizationId, formData.selectedOrganizationId, t],
  );

  // Load student subscriptions
  const loadStudentSubscriptions = useCallback(async (orgId, studentId) => {
    if (!studentId || !orgId) {
      setStudentSubscriptions([]);
      return;
    }

    setLoadingStudentSubscriptions(true);
    try {
      const response = await studentSubscriptionsAPI.getByStudent(
        orgId,
        studentId,
      );
      setStudentSubscriptions(response.data.data || []);
    } catch (error) {
      console.error("Error loading student subscriptions:", error);
      // Don't error blocking, just empty list
      setStudentSubscriptions([]);
    } finally {
      setLoadingStudentSubscriptions(false);
    }
  }, []);

  // Handle students selection
  const handleStudentsChange = useCallback(
    (selectedStudentIds) => {
      setFormData((prev) => ({
        ...prev,
        students: selectedStudentIds,
      }));

      if (selectedStudentIds.length > 0) {
        // Resolve the student's specific organizationId for more reliable subscription fetching
        // especially when operating in 'all' organizations mode.
        const firstStudentId = selectedStudentIds[0];
        const studentObj = modalStudents.find(s => s.id === firstStudentId);
        
        const targetOrgId = studentObj?.organizationId || formData.selectedOrganizationId || organizationId;
        
        loadStudentSubscriptions(targetOrgId, firstStudentId);
      } else {
        setStudentSubscriptions([]);
      }
    },
    [organizationId, formData.selectedOrganizationId, loadStudentSubscriptions, modalStudents],
  );

  // Validate form
  const validateForm = useCallback(() => {
    const errors = {};

    // When viewing all organizations, require organization selection
    if (isAllOrgsMode && !formData.selectedOrganizationId) {
      errors.selectedOrganizationId = t(
        "sessionsPage.validations.selectOrganization",
      );
    }

    if (!formData.trainerId) {
      errors.trainerId = t("sessionsPage.validations.trainerRequired");
    }

    if (!isDemoSession) {
      if (!formData.students || formData.students.length === 0) {
        errors.students = t("sessionsPage.validations.studentRequired");
      }

      if (!formData.sessionTypeId) {
        errors.sessionTypeId = t("sessionsPage.validations.packageRequired");
      } else {
        // Check max participants if package or session type is selected
        const selectedItem = sessionTypes.find(p => p.id === formData.sessionTypeId);
        if (selectedItem && selectedItem.maxParticipants) {
          if (formData.students.length > selectedItem.maxParticipants) {
            errors.students = t("sessionsPage.validations.maxParticipantsExceeded", {
              max: selectedItem.maxParticipants
            }) || `Bu paket/ders en fazla ${selectedItem.maxParticipants} kişi için geçerlidir.`;
          }
        }
      }
    }

    if (!formData.startTime) {
      errors.startTime = t("sessionsPage.validations.startTimeRequired");
    }

    if (!formData.endTime) {
      errors.endTime = t("sessionsPage.validations.endTimeRequired");
    }

    const startTime = moment(formData.startTime);
    const endTime = moment(formData.endTime);

    if (endTime.isSameOrBefore(startTime)) {
      errors.endTime = t("sessionsPage.validations.endTimeAfterStart");
    }

    // Past date check: only for creation
    if (modalType === "create") {
      const isPast = startTime.isBefore(moment().startOf("day"));
      if (isPast && !hasPermission("create:sessions:past")) {
        errors.startTime = t(
          "sessionsPage.validations.pastDateNotAllowed",
          "Geriye dönük seans oluşturma yetkiniz bulunmamaktadır.",
        );
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isAllOrgsMode, t, subPackages, isDemoSession, sessionTypes]);

  // Auto-fix selection for Groups if ID mismatch
  useEffect(() => {
    if (
      modalType === "group_manage" &&
      selectedEvent &&
      sessionTypes.length > 0
    ) {
      const currentId = formData.sessionTypeId;
      const matchById = sessionTypes.find((t) => t.id === currentId);

      const targetName =
        selectedEvent.packageName ||
        selectedEvent.students?.[0]?.session?.packageName ||
        selectedEvent.students?.[0]?.session?.sessionTypeName;

      if (!matchById && currentId && targetName) {
        console.log("Injecting missing session type option:", targetName);
        setSessionTypes((prev) => [
          ...prev,
          { id: currentId, name: targetName, isPackage: true },
        ]);
        return;
      }

      if (!matchById) {
        if (targetName) {
          const normalizedTarget = targetName.trim().toLowerCase();
          const matchByName = sessionTypes.find((t) => {
            const tName = (t.name || t.packageName || "").trim().toLowerCase();
            return tName === normalizedTarget;
          });

          if (matchByName) {
            console.log("Auto-matched session type by name:", matchByName.name);
            setFormData((prev) => {
              if (prev.sessionTypeId !== matchByName.id) {
                return { ...prev, sessionTypeId: matchByName.id };
              }
              return prev;
            });
          }
        }
      }

      // Auto-set Main Type ID if missing (for UI consistency)
      if (formData.sessionTypeId && !formData.mainTypeId) {
        // Check if it's a package
        const pkg = subPackages.find((p) => p.id === formData.sessionTypeId);
        if (pkg && pkg.sessionTypeId) {
          setFormData((prev) => ({ ...prev, mainTypeId: pkg.sessionTypeId }));
        } else {
          // Check if it's a raw session type
          const type = mainSessionTypes.find(
            (t) => t.id === formData.sessionTypeId,
          );
          if (type) {
            setFormData((prev) => ({ ...prev, mainTypeId: type.id }));
          }
        }
      }
    }
  }, [
    sessionTypes,
    modalType,
    selectedEvent,
    formData.sessionTypeId,
    formData.mainTypeId,
    subPackages,
    mainSessionTypes,
  ]);

  // Session time management
  const addSessionTime = useCallback(() => {
    const now = moment();
    const newSession = {
      id: Date.now(),
      startTime: now.format("YYYY-MM-DDTHH:mm:ss"),
      endTime: now.add(55, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
    };
    setSessionTimes((prev) => [...prev, newSession]);
  }, []);

  const removeSessionTime = useCallback((sessionId) => {
    setSessionTimes((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const updateSessionTime = useCallback((sessionId, field, value) => {
    setSessionTimes((prev) =>
      prev.map((session) => {
        if (session.id === sessionId) {
          if (field === "startTime") {
            return {
              ...session,
              startTime: value,
              endTime:
                session.endTime ||
                moment(value).add(55, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
            };
          } else if (field === "endTime") {
            return {
              ...session,
              endTime: value,
            };
          }
        }
        return session;
      }),
    );
  }, []);

  // Session creation is always allowed
  const canCreateSession = true;

  // Modal actions
  const openCreateModal = useCallback(
    (date, preselectedTrainerId) => {
      let startTime;

      if (date) {
        const mDate = moment(date);
        if (mDate.hours() === 0 && mDate.minutes() === 0) {
          startTime = mDate.hours(9).minutes(0).seconds(0);
        } else {
          startTime = mDate.seconds(0);
        }
      } else {
        startTime = moment().hours(9).minutes(0).seconds(0);
      }

      const endTime = moment(startTime).add(55, "minutes"); // Default duration: 55 minutes

      let defaultTrainerId = "";
      if (preselectedTrainerId && preselectedTrainerId !== "all") {
        defaultTrainerId = preselectedTrainerId;
      } else if (user?.email) {
        const matchedTrainer = trainers.find((t) => t.email === user.email);
        if (matchedTrainer) defaultTrainerId = matchedTrainer.id;
      }

      setFormData({
        selectedOrganizationId: "",
        trainerId: defaultTrainerId,
        students: [],
        sessionTypeId: "",
        mainTypeId: "",
        startTime: startTime.format("YYYY-MM-DDTHH:mm:ss"),
        endTime: endTime.format("YYYY-MM-DDTHH:mm:ss"),
        notes: "",
        status: "scheduled",
      });

      setSessionTimes([
        {
          id: 1,
          startTime: startTime.format("YYYY-MM-DDTHH:mm:ss"),
          endTime: endTime.format("YYYY-MM-DDTHH:mm:ss"),
        },
      ]);

      setModalType("create");
      setSelectedEvent(null);
      setFormErrors({});
      setFormError(null);

      if (isAllOrgsMode) {
        loadOrganizations();
      } else {
        loadSessionTypes();
      }

      setShowModal(true);
      return true;
    },
    [trainers, loadSessionTypes, loadOrganizations, isAllOrgsMode, user],
  );

  const openGroupManageModal = useCallback(
    (groupSession) => {
      const representativeSession =
        groupSession.students?.find(
          (s) => s.session?.sessionTypeId || s.session?.membershipPackageId,
        )?.session ||
        groupSession.students?.[0]?.session ||
        {};

      const sessionTypeId =
        representativeSession.membershipPackageId ||
        representativeSession.sessionTypeId ||
        "";

      let orgId =
        representativeSession.organizationId ||
        groupSession.organizationId ||
        "";

      if (!orgId && groupSession.trainerId && trainers.length > 0) {
        const trainer = trainers.find((t) => t.id === groupSession.trainerId);
        if (trainer?.organizationId) {
          orgId = trainer.organizationId;
        }
      }

      setFormData({
        selectedOrganizationId: orgId,
        trainerId: groupSession.trainerId,
        students: groupSession.students.map((s) => s.studentId),
        sessionTypeId: sessionTypeId,
        mainTypeId: "", // Will be set in UI based on sessionTypeId
        startTime: moment(groupSession.start).format("YYYY-MM-DDTHH:mm:ss"),
        endTime: moment(groupSession.end).format("YYYY-MM-DDTHH:mm:ss"),
        notes: "",
        status: groupSession.status || "scheduled",
      });

      setModalType("group_manage");
      setSelectedEvent(groupSession);
      setFormErrors({});
      setFormError(null);

      loadSessionTypes(orgId || organizationId);

      // Load subscriptions if single student (optional for group manage?)
      if (groupSession.students.length === 1) {
        loadStudentSubscriptions(
          orgId || organizationId,
          groupSession.students[0].studentId,
        );
      }

      setShowModal(true);
    },
    [loadSessionTypes, organizationId, trainers, loadStudentSubscriptions],
  );

  const openEditModal = useCallback(
    (session) => {
      const orgId = session.organizationId || "";
      setFormData({
        selectedOrganizationId: orgId,
        trainerId: session.trainerId,
        students: [session.studentId],
        sessionTypeId: session.sessionTypeId || "",
        mainTypeId: "", // Will be set in UI
        startTime: moment(session.start).format("YYYY-MM-DDTHH:mm:ss"),
        endTime: moment(session.end).format("YYYY-MM-DDTHH:mm:ss"),
        notes: session.notes || "",
        status: session.status || "scheduled",
      });

      setModalType("edit");
      setSelectedEvent(session);
      setFormErrors({});
      setFormError(null);

      loadSessionTypes(orgId);
      loadStudentSubscriptions(orgId, session.studentId);

      setShowModal(true);
    },
    [loadSessionTypes, loadStudentSubscriptions],
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedEvent(null);
    resetFormData();
  }, [resetFormData]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (data, onSuccess) => {
      if (!validateForm()) {
        return;
      }

      setSubmitting(true);
      setFormError(null);

      const targetOrgId = isAllOrgsMode
        ? formData.selectedOrganizationId || selectedEvent?.organizationId
        : organizationId;

      let finalSessionTypeId = formData.sessionTypeId;
      let isBySessionType = false;
      let finalStudents = formData.students;

      if (isDemoSession) {
        finalSessionTypeId = formData.mainTypeId;
        isBySessionType = true;
        finalStudents = [];
      } else {
        const selectedType = sessionTypes.find(
          (t) => t.id === formData.sessionTypeId,
        );
        isBySessionType = selectedType?.isPackage === false;
      }

      try {
        const promises = [];

        if (modalType === "create") {
          const sessionData = {
            trainerId: formData.trainerId,
            students: finalStudents,
            [isBySessionType ? "sessionTypeId" : "membershipPackageId"]:
              finalSessionTypeId,
            startTime: moment(formData.startTime).toISOString(),
            endTime: moment(formData.endTime).toISOString(),
            notes: formData.notes,
            status: formData.status,
            isBySessionType,
          };

          const response = await sessionsAPI.create(targetOrgId, sessionData);

          if (response.data.data && response.data.data.errorCount > 0) {
            const successCount = response.data.data.successCount;
            const errorCount = response.data.data.errorCount;
            const errors = response.data.data.errors || [];
            // Surface every distinct reason, not just the first — a group create can
            // fail for different reasons per student (expired, exhausted, no package).
            const distinctErrors = [...new Set(errors.map((e) => e && e.error).filter(Boolean))];
            const errorMsg = distinctErrors.length > 0 ? distinctErrors.join(" • ") : t("sessionsPage.messages.creationFailed");

            if (successCount === 0) {
              setFormError(errorMsg);
              return; // Do not call onSuccess to prevent modal closure
            } else {
              onSuccess(
                `${t("sessionsPage.messages.createdPartial", {
                  success: successCount,
                  error: errorCount,
                })} (${errorMsg})`
              );
            }
          } else {
            const count =
              response.data.data?.successCount || formData.students.length || 1;
            onSuccess(t("sessionsPage.messages.created", { count }));
            // Note: Backend handles notifications for session creation (trainer + student)
          }
        } else {
          if (modalType === "group_manage") {
            const originalStudents = selectedEvent.students;
            const newStudentIds = formData.students;

            const addedStudentIds = newStudentIds.filter(
              (nid) => !originalStudents.some((os) => os.studentId === nid),
            );

            const removedStudents = originalStudents.filter(
              (os) => !newStudentIds.includes(os.studentId),
            );

            const existingStudents = originalStudents.filter((os) =>
              newStudentIds.includes(os.studentId),
            );

            if (addedStudentIds.length > 0) {
              const createPayload = {
                trainerId: formData.trainerId,
                students: addedStudentIds,
                [isBySessionType ? "sessionTypeId" : "membershipPackageId"]:
                  finalSessionTypeId,
                startTime: moment(formData.startTime).toISOString(),
                endTime: moment(formData.endTime).toISOString(),
                notes: formData.notes,
                status: formData.status,
                isBySessionType,
              };
              promises.push(sessionsAPI.create(targetOrgId, createPayload));
            }

            removedStudents.forEach((rs) => {
              if (rs.session?.id) {
                promises.push(sessionsAPI.delete(targetOrgId, rs.session.id));
              }
            });

            existingStudents.forEach((es) => {
              if (es.session?.id) {
                // IMPORTANT: We only update group-wide fields for existing students.
                // We EXCLUDE status and notes to avoid overwriting individual student attendance/notes
                // unless we implement a specific bulk-update toggle in the future.
                const updatePayload = {
                  trainerId: formData.trainerId,
                  [isBySessionType ? "sessionTypeId" : "membershipPackageId"]:
                    finalSessionTypeId,
                  startTime: moment(formData.startTime).toISOString(),
                  endTime: moment(formData.endTime).toISOString(),
                  isBySessionType,
                };
                promises.push(
                  sessionsAPI.update(targetOrgId, es.session.id, updatePayload),
                );
              }
            });
          } else if (modalType === "edit") {
            const sessionData = {
              trainerId: formData.trainerId,
              studentId: finalStudents[0] || null,
              [isBySessionType ? "sessionTypeId" : "membershipPackageId"]:
                finalSessionTypeId,
              startTime: moment(formData.startTime).toISOString(),
              endTime: moment(formData.endTime).toISOString(),
              notes: formData.notes,
              status: formData.status,
              isBySessionType,
            };

            promises.push(
              sessionsAPI.update(targetOrgId, selectedEvent.id, sessionData),
            );
          }

          await Promise.all(promises);
          onSuccess(t("sessionsPage.messages.updated"));
        }

        await loadSessions();
      } catch (error) {
        console.error("Error saving session:", error);
        const errorMessage =
          error.response?.data?.error || t("sessionsPage.messages.saveError");

        if (error.response?.data?.details) {
          setFormError(
            `${errorMessage}: ${JSON.stringify(error.response.data.details)}`,
          );
        } else {
          setFormError(errorMessage);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData,
      modalType,
      selectedEvent,
      organizationId,
      validateForm,
      loadSessions,
      isAllOrgsMode,
      t,
      sessionTypes,
      students,
      trainers,
      user,
    ],
  );

  const handleDeleteSession = useCallback(
    async (onSuccess) => {
      if (!selectedEvent) return;

      setSubmitting(true);

      try {
        await sessionsAPI.delete(selectedEvent.organizationId || organizationId, selectedEvent.id);
        await loadSessions();
        onSuccess(t("sessionsPage.messages.deleted"));
      } catch (error) {
        console.error("Error deleting session:", error);
        setFormError(t("sessionsPage.messages.deleteError"));
      } finally {
        setSubmitting(false);
      }
    },
    [selectedEvent, organizationId, loadSessions, t],
  );

  return {
    showModal,
    modalType,
    selectedEvent,
    submitting,
    formError,
    formErrors,
    formData,
    sessionTimes,
    sessionTypes,
    mainSessionTypes, // Exported
    subPackages, // Exported
    loadingSessionTypes,
    studentSubscriptions, // Exported
    loadingStudentSubscriptions, // Exported
    organizations,
    loadingOrganizations,
    isAllOrgsMode,
    isDemoSession,
    loadSessionTypes,
    modalStudents,
    modalTrainers,
    canCreateSession,
    openCreateModal,
    openEditModal,
    openGroupManageModal,
    closeModal,
    handleSubmit,
    handleDeleteSession,
    setFormData,
    setSessionTimes,
    addSessionTime,
    removeSessionTime,
    updateSessionTime,
    handleStudentsChange,
    resetFormData,
  };
};
